import { LastQuotesFilters } from 'src/app/modules/marketing/products/entities/last-quotes-filters.class';
import { Component, OnInit, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TouchpointsService } from 'src/app/modules/crm/touchpoints/services/touchpoints.service';
import { QuoteDetailsService } from 'src/app/modules/pos/quotes/services/quote-details.service';
import { Customer } from 'src/app/modules/shared/entities/customer.class';
import { AppInfoService } from 'core/app-info/app-info.service';
import { countriesWithNoCurrencyDisplayed } from 'core/app-info/country-specifics.enum';
import { QuoteTemplateValues } from 'src/app/modules/shared/entities/quote-template-values.class';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
  selector: 'app-last-quotes-filters',
  templateUrl: './last-quotes-filters.component.html',
  styleUrls: ['./last-quotes-filters.component.scss'],
  standalone: false
})
export class LastQuotesFiltersComponent implements OnInit {
  filterFormGroup = new FormGroup({
    startDate: new FormControl(''),
    endDate: new FormControl(''),
    customerName: new FormControl(''),
    bpNumber: new FormControl(''),
    quoteName: new FormControl(''),
    quoteNumber: new FormControl(''),
    confidence: new FormControl(''),
    dollarAmount: new FormControl(''),
    expiringInDays: new FormControl(),
    dollarRangeMin: new FormControl(''),
    dollarRangeMax: new FormControl('')
  });

  appStrings: AppLocalization;
  dsDateFormat = this.appInfoService.dsDateFormat;
  displayedTextAmount: string;
  displayedTextRangesFrom: string;
  displayedTextRangesTo: string;
  countryCode = '';
  displayCurrency = false;
  formHasValue = false;
  prettyStartDateValue = 'Start Date';
  prettyEndDateValue = 'End Date';
  prettyStartDate = this.prettyStartDateValue;
  prettyEndDate = this.prettyEndDateValue;
  today = new Date();
  confidenceOptions: { key: number, value: string }[] = [];
  currencySymbol = '';
  isMobile = false;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {filters: LastQuotesFilters, customer?: Customer, isCustomerDashboard?: boolean, isDs?: boolean},
    private appInfoService: AppInfoService,
    private touchpointsService: TouchpointsService,
    private quoteDetailsService: QuoteDetailsService,
    private dialogRef: MatDialogRef<LastQuotesFiltersComponent>,
    private deviceService: DeviceDetectorService

  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();
    this.appStrings = this.appInfoService.appStrings;
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.quoteDetailsService.getStaticValues(([templateValues]: QuoteTemplateValues[]) => {
      if (templateValues.ConfidenceToClose) {
        this.confidenceOptions = Object.keys(templateValues.ConfidenceToClose).map(key => ({
          key: Number(key), value: key, label: templateValues.ConfidenceToClose[key]
        }));
      }
    });

    if (this.data.filters) {
      this.filterFormGroup.patchValue(this.data.filters);

      if (this.data.filters.startDate) {
        this.prettyStartDate = this.touchpointsService.getDateLabel(this.data.filters.startDate);
      }

      if (this.data.filters.endDate) {
        this.prettyEndDate = this.touchpointsService.getDateLabel(this.data.filters.endDate);
      }
    }

    if (this.data.customer) {
      const customer = this.data.customer;
      this.filterFormGroup.controls.customerName.setValue(customer.name);
      this.filterFormGroup.controls.bpNumber.setValue(customer.bpNumber);
      this.filterFormGroup.controls.customerName.disable();
      this.filterFormGroup.controls.bpNumber.disable();
    }
    this.countryCode = this.appInfoService.userData?.UserInfo?.CountryCode || 'US';
    this.determineAmountTextToDisplay();

    this.filterFormGroup.valueChanges.subscribe((newVal: Partial<LastQuotesFilters>) => this.updateFormHasValue(newVal));
    this.dialogRef.beforeClosed().subscribe(() => {
      if (!this.formHasValue && this.filterFormGroup.dirty) {
        this.dialogRef.close({ clearAllFilters: true });
      }
    });
    this.updateFormHasValue(this.filterFormGroup.value);
  }

  /**
   * Determine amount and ranges text by country
   */
  determineAmountTextToDisplay() {
    if (countriesWithNoCurrencyDisplayed[this.countryCode]) {
      this.displayedTextAmount = this.appStrings.quoteSearch_amount;
      this.displayedTextRangesFrom = this.appStrings.quoteSearch_rangesFrom;
      this.displayedTextRangesTo = this.appStrings.quoteSearch_rangesTo;
      this.displayCurrency = false;
    } else {
      this.displayedTextAmount = this.appStrings.quoteSearch_dollarAmount;
      this.displayedTextRangesFrom = this.appStrings.quoteSearch_dollarRangeFrom;
      this.displayedTextRangesTo = this.appStrings.quoteSearch_dollarRangeTo;
      this.displayCurrency = true;
    }
  }

  /**
   * Searches from the search textbox
   */
  searchText() {
    const filters: LastQuotesFilters = {
      ...this.filterFormGroup.value,
      prettyStartDate: this.filterFormGroup.value.startDate
        ? this.prettyStartDate
        : '',
      prettyEndDate: this.filterFormGroup.value.endDate
        ? this.prettyEndDate
        : '',
    };

    if (this.filterFormGroup.dirty) {
      this.dialogRef.close(filters);
    } else {
      this.dialogRef.close();
    }
  }

  /**
   * Clears the value from given control name input
   * @param controlName - the control name for which the value needs to be removed
   */
  clearFilter(controlName: string) {
    this.filterFormGroup.controls[controlName].setValue('');
    this.filterFormGroup.markAsDirty();
  }

  /**
   * Updates the status of the formHasValue flag
   */
  private updateFormHasValue(newVal: Partial<LastQuotesFilters>) {
    this.formHasValue = false;

    for (const value of Object.values(newVal)) {
      if (value) {
        this.formHasValue = true;
      }
    }
  }

  /**
   * Clears the value from all controls input
   */
  clearAllFilters() {
    this.filterFormGroup.patchValue({
      startDate: '',
      endDate: '',
      quoteName: '',
      quoteNumber: '',
      confidence: '',
      dollarAmount: '',
      expiringInDays: '',
      dollarRangeMin: '',
      dollarRangeMax: ''
    });

    if (!this.data.customer) {
      this.filterFormGroup.patchValue({
        customerName: '',
        bpNumber: ''
      });
    }
    this.filterFormGroup.markAsDirty();
    this.prettyStartDate = this.prettyStartDateValue;
    this.prettyEndDate = this.prettyEndDateValue;
  }

  /**
   * Update calendar label with pretty start date
   * @param date - selected date
   */
  updateCalendarLabelStartDate(date: Date) {
    this.filterFormGroup.controls.startDate.setValue(new Date(date).toISOString());
    this.prettyStartDate = this.touchpointsService.getDateLabel(date);
  }

  /**
   * Update calendar label with pretty end date and pretty start date when needed
   * @param date - selected date
   */
  updateCalendarLabelEndDate(date: Date) {
    this.filterFormGroup.controls.endDate.setValue(new Date(date).toISOString());
    this.prettyEndDate = this.touchpointsService.getDateLabel(date);
    if (this.filterFormGroup.controls.startDate.value > this.filterFormGroup.controls.endDate.value) {
      this.updateCalendarLabelStartDate(date);
    }
  }

}
